// "New Handover" dialog (nursing-ops sprint, Epic 9 / D11). Creates a draft
// ShiftHandover; the backend auto-populates one SBAR row per admitted
// patient on the chosen ward (main/ipd/manage_shift_handover.py post()).
import React, { useState } from "react";
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import { toast } from "react-toastify";

import { NURSE_COLORS } from "../theme";
import { createShiftHandover } from "../api/nurseApi";
import { useShiftSchedule } from "../hooks";


function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const EMPTY = { shiftDate: todayStr(), outgoingShift: "", incomingShift: "", wardCode: "" };

export default function ShiftHandoverCreateDialog({ open, onClose, wards, handedOverBy, onCreated }) {
  const { schedule } = useShiftSchedule();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const canSubmit =
    form.shiftDate && form.outgoingShift && form.incomingShift && form.wardCode && handedOverBy && !submitting;

  const handleClose = () => {
    if (submitting) return;
    setForm(EMPTY);
    onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await createShiftHandover({
        handed_over_by: handedOverBy,
        shift_date: form.shiftDate,
        outgoing_shift: form.outgoingShift,
        incoming_shift: form.incomingShift,
        ward_id: form.wardCode,
      });
      if (res?.status !== "Success") {
        toast.error(res?.message || "Failed to create handover");
        return;
      }
      toast.success(res?.message || "Handover created");
      setForm(EMPTY);
      onCreated?.(res.id);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to create handover");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: NURSE_COLORS.primary }}>New Shift Handover</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            type="date" size="small" label="Shift date" value={form.shiftDate}
            onChange={(e) => set({ shiftDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            select size="small" label="Ward" value={form.wardCode}
            onChange={(e) => set({ wardCode: e.target.value })}
          >
            {wards.map((w) => (<MenuItem key={w.code} value={w.code}>{w.name}</MenuItem>))}
            {wards.length === 0 && <MenuItem value="" disabled>No wards available</MenuItem>}
          </TextField>
          <TextField
            select size="small" label="Outgoing shift" value={form.outgoingShift}
            onChange={(e) => set({ outgoingShift: e.target.value })}
          >
            {schedule.map((s) => (<MenuItem key={s.code} value={s.code}>{s.label}</MenuItem>))}
          </TextField>
          <TextField
            select size="small" label="Incoming shift" value={form.incomingShift}
            onChange={(e) => set({ incomingShift: e.target.value })}
          >
            {schedule.map((s) => (<MenuItem key={s.code} value={s.code}>{s.label}</MenuItem>))}
          </TextField>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Handing over as: {handedOverBy || "—"}
            </Typography>
          </Box>
          {!handedOverBy && <Alert severity="warning">Could not resolve your nurse ID — cannot create a handover.</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitting} variant="outlined">Cancel</Button>
        <Button onClick={handleSubmit} disabled={!canSubmit} variant="contained">
          {submitting ? "Creating..." : "Create Handover"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
